/**
 * User-Agent 解析工具
 * 从 navigator.userAgent 提取设备、操作系统、容器信息
 */

export interface UAParseResult {
  deviceType: string
  deviceBrand: string
  deviceModel: string
  os: string
  containerType: string
  containerVersion: string
  webviewVersion: string
}

/**
 * 解析 User-Agent 字符串
 */
export function parseUserAgent(ua?: string): UAParseResult {
  const userAgent = ua || (typeof navigator !== 'undefined' ? navigator.userAgent : '')

  const result: UAParseResult = {
    deviceType: '设备类型未知',
    deviceBrand: '设备品牌未知',
    deviceModel: '设备型号未知',
    os: '操作系统未知',
    containerType: '',
    containerVersion: '',
    webviewVersion: '',
  }

  if (!userAgent) return result

  // 设备类型检测
  if (/iPad/i.test(userAgent)) {
    result.deviceType = 'pad'
    result.deviceBrand = 'apple'
    result.deviceModel = 'iPad'
  } else if (/iPhone/i.test(userAgent)) {
    result.deviceType = 'mobile'
    result.deviceBrand = 'apple'
    result.deviceModel = 'iPhone'
  } else if (/Android|HarmonyOS|OpenHarmony/i.test(userAgent)) {
    result.deviceType = /Mobile/i.test(userAgent) ? 'mobile' : 'pad'
    // 国内主流 Top10 品牌识别表：[品牌标识, UA关键词正则, 型号提取正则]
    const brandRules: Array<[string, RegExp, RegExp]> = [
      ['xiaomi',  /\b(?:XiaoMi|Xiaomi|Redmi|POCO)\b/i,        /\b(?:XiaoMi|Xiaomi|Redmi|POCO)[\s/]*(\S+)/i],
      ['huawei',  /\bHUAWEI\b/i,                               /\bHUAWEI[\s/]*(\S+)/i],
      ['honor',   /\bHONOR\b/i,                                 /\bHONOR[\s/]*(\S+)/i],
      ['oppo',    /\bOPPO\b/i,                                  /\bOPPO[\s/]*(\S+)/i],
      ['vivo',    /\bvivo\b/i,                                   /\bvivo[\s/]*(\S+)/i],
      ['meizu',   /\b(?:Meizu|MZ)\b/i,                          /\b(?:Meizu|MZ)[\s/]*(\S+)/i],
      ['lenovo',  /\b(?:Lenovo|ZUK)\b/i,                        /\b(?:Lenovo|ZUK)[\s/]*(\S+)/i],
      ['zte',     /\bZTE\b/i,                                    /\bZTE[\s/]*(\S+)/i],
      ['gionee',  /\bGionee\b/i,                                 /\bGionee[\s/]*(\S+)/i],
      ['coolpad', /\bCoolpad\b/i,                                /\bCoolpad[\s/]*(\S+)/i],
    ]

    let brandMatched = false
    for (const [brand, testRe, modelRe] of brandRules) {
      if (testRe.test(userAgent)) {
        result.deviceBrand = brand
        const modelMatch = userAgent.match(modelRe)
        if (modelMatch) {
          result.deviceModel = modelMatch[1]
        }
        brandMatched = true
        break
      }
    }

    // 未命中 Top10 品牌，保持默认 "设备品牌未知"
    if (!brandMatched) {
      // 尝试从 UA 中提取型号信息供参考
      const androidMatch = userAgent.match(/(?:Android|HarmonyOS)[^;]*;\s*([^)]+)\)/)
      if (androidMatch) {
        const deviceInfo = androidMatch[1].replace(/\s*Build\/.*$/i, '').trim()
        if (deviceInfo) {
          result.deviceModel = deviceInfo
        }
      }
    }
  } else if (/Windows|Macintosh|Linux/i.test(userAgent)) {
    result.deviceType = 'pc'
    if (/Macintosh/i.test(userAgent)) {
      result.deviceBrand = 'apple'
      result.deviceModel = 'Mac'
    }
    // Windows / Linux 无法从 UA 中获取设备品牌，保持默认值 '设备品牌未知'
  }

  // 操作系统检测
  if (/Windows NT 10/i.test(userAgent)) {
    result.os = 'Windows 10/11'
  } else if (/Windows NT 6\.3/i.test(userAgent)) {
    result.os = 'Windows 8.1'
  } else if (/Windows NT 6\.2/i.test(userAgent)) {
    result.os = 'Windows 8'
  } else if (/Windows NT 6\.1/i.test(userAgent)) {
    result.os = 'Windows 7'
  } else if (/Mac OS X ([\d_.]+)/i.test(userAgent)) {
    const match = userAgent.match(/Mac OS X ([\d_.]+)/i)
    result.os = `macOS ${match?.[1]?.replace(/_/g, '.') || ''}`
  } else if (/iPhone OS ([\d_]+)/i.test(userAgent)) {
    const match = userAgent.match(/iPhone OS ([\d_]+)/i)
    result.os = `iOS ${match?.[1]?.replace(/_/g, '.') || ''}`
  } else if (/HarmonyOS[\s/]?([\d.]*)/i.test(userAgent)) {
    const match = userAgent.match(/HarmonyOS[\s/]?([\d.]*)/i)
    result.os = `HarmonyOS ${match?.[1] || ''}`.trim()
  } else if (/OpenHarmony[\s/]?([\d.]*)/i.test(userAgent)) {
    const match = userAgent.match(/OpenHarmony[\s/]?([\d.]*)/i)
    result.os = `OpenHarmony ${match?.[1] || ''}`.trim()
  } else if (/Android ([\d.]+)/i.test(userAgent)) {
    const match = userAgent.match(/Android ([\d.]+)/i)
    result.os = `Android ${match?.[1] || ''}`
  } else if (/Linux/i.test(userAgent)) {
    result.os = 'Linux'
  }

  // 容器版本检测（从浏览器版本推断）
  if (/Edg\/([\d.]+)/i.test(userAgent)) {
    const match = userAgent.match(/Edg\/([\d.]+)/i)
    result.containerVersion = match?.[1] || ''
  } else if (/Chrome\/([\d.]+)/i.test(userAgent) && !/Edg/i.test(userAgent)) {
    const match = userAgent.match(/Chrome\/([\d.]+)/i)
    result.containerVersion = match?.[1] || ''
  } else if (/Firefox\/([\d.]+)/i.test(userAgent)) {
    const match = userAgent.match(/Firefox\/([\d.]+)/i)
    result.containerVersion = match?.[1] || ''
  } else if (/Safari\/([\d.]+)/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    const versionMatch = userAgent.match(/Version\/([\d.]+)/i)
    result.containerVersion = versionMatch?.[1] || ''
  }

  /**
   * 宿主 WebView 检测
   * 默认为浏览器 h5_browser
   * 当宿主是小程序时，containerType 会变为 mini_program
   * 当宿主是 web iframe嵌套时，containerType 会变为 h5_webview
   * 当宿主是 app 时，containerType 会变为 harmony_app / ios_app / android_app
   */
  // 平台检测（提前声明，WebView 检测需要使用）
  const isHarmony = /HarmonyOS|OpenHarmony/i.test(userAgent)
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent)

  const isMiniProgram = /miniProgram|swan\/|toutiaomicroapp|QuarkMiniApp|AlipayClient|DingTalk/i.test(userAgent)
  // Android WebView: UA 含 wv 或 WebView
  // iOS WKWebView: UA 含 AppleWebKit 但不含 Safari/（原生 Safari 一定有 Safari/xxx）
  const isAndroidWebView = /\bwv\b|WebView/i.test(userAgent)
  const isIOSWebView = isIOS && /AppleWebKit/i.test(userAgent) && !/Safari\//i.test(userAgent)
  const isWebView = (isAndroidWebView || isIOSWebView) && result.deviceType !== 'pc'
  let isIframe = false
  try {
    isIframe = typeof window !== 'undefined' && window.self !== window.top
  } catch {
    // 跨域 iframe 中访问 window.top 会抛出异常，说明确实在 iframe 中
    isIframe = true
  }

  if (isMiniProgram) {
    // 小程序环境优先级最高
    result.containerType = 'mini_program'
  } else if (isWebView) {
    // 原生 App 内嵌 WebView，根据操作系统区分
    if (isHarmony) {
      result.containerType = 'harmony_app'
    } else if (isIOS) {
      result.containerType = 'ios_app'
    } else {
      result.containerType = 'android_app'
    }
  } else if (isIframe) {
    // iframe 嵌套
    result.containerType = 'h5_webview'
  }

  // 获取 WebView 版本（所有容器场景通用：小程序、App WebView、iframe）
  if (isHarmony) {
    // 鸿蒙：优先匹配 ArkWeb 内核，降级到 Chrome
    const arkMatch = userAgent.match(/ArkWeb\/([\d.]+)/i)
    const chromeMatch = userAgent.match(/Chrome\/([\d.]+)/i)
    result.webviewVersion = arkMatch?.[1] || chromeMatch?.[1] || ''
  } else if (isIOS) {
    // iOS WKWebView：从 AppleWebKit 版本提取
    const wkMatch = userAgent.match(/AppleWebKit\/([\d.]+)/i)
    result.webviewVersion = wkMatch?.[1] || ''
  } else {
    // Android / 其他：使用 Chrome 内核版本
    const chromeMatch = userAgent.match(/Chrome\/([\d.]+)/i)
    result.webviewVersion = chromeMatch?.[1] || ''
  }

  return result
}

/**
 * 获取屏幕方向
 */
export function getScreenOrientation(): string {
  if (typeof screen !== 'undefined' && screen.orientation) {
    return screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape'
  }
  if (typeof window !== 'undefined') {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  }
  return 'portrait'
}

/**
 * 获取系统主题
 */
export function getOsTheme(): string {
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    }
  }
  return 'auto'
}

/**
 * 获取网络类型
 */
export function getNetworkType(): string {
  if (typeof navigator !== 'undefined') {
    const connection = (navigator as Navigator & { connection?: { type?: string; effectiveType?: string } }).connection
    if (connection) {
      // 优先使用 type 获取真实网络类型（wifi/cellular/bluetooth/ethernet/none）
      if (connection.type) {
        const typeMap: Record<string, string> = {
          wifi: 'wifi',
          cellular: 'cellular', // 具体代数由 effectiveType 补充
          bluetooth: 'bluetooth',
          ethernet: 'ethernet',
          none: 'none',
        }
        const mapped = typeMap[connection.type]
        // cellular 时进一步用 effectiveType 区分 2g/3g/4g/5g
        if (mapped === 'cellular' && connection.effectiveType) {
          return connection.effectiveType
        }
        if (mapped) return mapped
      }
      // type 不可用时降级为 effectiveType
      if (connection.effectiveType) {
        return connection.effectiveType
      }
    }
  }
  return '网络类型未知'
}

/**
 * 获取系统语言
 */
export function getOsLanguage(): string {
  if (typeof navigator !== 'undefined') {
    return navigator.language || '系统语言未知'
  }
  return '系统语言未知'
}

/**
 * 获取系统地区（从 navigator.language 提取）
 */
export function getOsRegion(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    const parts = navigator.language.split('-')
    return parts.length > 1 ? parts[1] : parts[0]
  }
  return '系统地区未知'
}

/**
 * 获取时区
 */
export function getTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '时区未知'
  } catch {
    return '时区未知'
  }
}

/**
 * 获取系统字体大小档位
 * H5 环境根据根元素字体大小推断
 */
export function getOsFontSize(): string {
  if (typeof document !== 'undefined') {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
    if (fontSize <= 12) return 'small'
    if (fontSize <= 16) return 'normal'
    if (fontSize <= 20) return 'large'
    return 'xlarge'
  }
  return 'normal'
}
